import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import { fetchSinglePromptRequest } from 'actions';
import {
  deletePromptTemplateFailure,
  deletePromptTemplateRequest,
  deletePromptTemplateSuccess,
  fetchPromptTemplatesFailure,
  fetchPromptTemplatesRequest,
  fetchPromptTemplatesSuccess,
  fetchSinglePromptTemplateRequest,
  fetchSinglePromptTemplateSuccess,
  upsertPromptTemplateFailure,
  upsertPromptTemplateRequest,
  upsertPromptTemplateSuccess,
} from 'actions/promptTemplates';

import { API, ErrorObject, LLMPromptTemplate, UUID } from 'types';

type EntityState = {
  loading: string[];
  error: ErrorObject | null;
  templates: LLMPromptTemplate[];
  activeTemplate: LLMPromptTemplate | null;
  promptTemplatesCount: number;
};

const defaultState: EntityState = {
  loading: [],
  error: null,
  templates: [],
  activeTemplate: null,
  promptTemplatesCount: 0,
};
type PromptTemplateReducer<P = void> = (
  state: EntityState,
  payload: PayloadAction<P>
) => EntityState;

const handleFetchSinglePromptTemplateRequest: PromptTemplateReducer = (state) => ({
  ...state,
  loading: [...state.loading, fetchSinglePromptRequest.toString()],
});

const handleFetchSinglePromptTemplateSuccess: PromptTemplateReducer<LLMPromptTemplate> = (
  state,
  { payload }
) => ({
  ...state,
  templates: [...state.templates, payload],
  activeTemplate: payload,
  loading: state.loading.filter((s) => s !== fetchSinglePromptTemplateRequest.toString()),
});

const handleFetchSinglePromptTemplateFailure: PromptTemplateReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchSinglePromptTemplateRequest.toString()),
});

const handleUpsertPromptTemplateRequest: PromptTemplateReducer = (state) => ({
  ...state,
  loading: [...state.loading, upsertPromptTemplateRequest.toString()],
});

const handleUpsertPromptTemplateSuccess: PromptTemplateReducer<LLMPromptTemplate> = (
  state,
  { payload }
) => {
  const template = state.templates.find((p) => p.uuid === payload.uuid);

  if (template) {
    return {
      ...state,
      templates: state.templates.map((p) => (p.uuid === payload.uuid ? { ...p, ...payload } : p)),
      activeTemplate: { ...template, ...payload },
      loading: state.loading.filter((s) => s !== upsertPromptTemplateRequest.toString()),
    };
  }

  return {
    ...state,
    templates: [...state.templates, payload],
    loading: state.loading.filter((s) => s !== upsertPromptTemplateRequest.toString()),
  };
};

const handleUpsertPromptTemplateFailure: PromptTemplateReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== upsertPromptTemplateRequest.toString()),
});

const handleDeletePromptTemplateRequest: PromptTemplateReducer = (state) => ({
  ...state,
  loading: [...state.loading, deletePromptTemplateRequest.toString()],
});

const handleDeletePromptTemplateSuccess: PromptTemplateReducer<{ uuid: UUID }> = (
  state,
  { payload }
) => {
  const newTemplates = state.templates.filter((p) => p.uuid !== payload.uuid);
  return {
    ...state,
    templates: newTemplates,
    loading: state.loading.filter((s) => s !== deletePromptTemplateRequest.toString()),
  };
};

const handleDeletePromptTemplateFailure: PromptTemplateReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== deletePromptTemplateRequest.toString()),
});

const handleFetchPromptTemplatesRequest: PromptTemplateReducer = (state) => ({
  ...state,
  loading: [...state.loading, fetchPromptTemplatesRequest.toString()],
});

const handleFetchPromptTemplatesSuccess: PromptTemplateReducer<
  API.WrappedAPIResponse<LLMPromptTemplate>
> = (state, { payload }) => ({
  ...state,
  templates: payload.records,
  promptTemplatesCount: payload.count,
  loading: state.loading.filter((s) => s !== fetchPromptTemplatesRequest.toString()),
});

const handleFetchPromptTemplatesFailure: PromptTemplateReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== fetchPromptTemplatesRequest.toString()),
});

const handlers = {
  [fetchPromptTemplatesRequest.toString()]: handleFetchPromptTemplatesRequest,
  [fetchPromptTemplatesFailure.toString()]: handleFetchPromptTemplatesFailure,
  [fetchPromptTemplatesSuccess.toString()]: handleFetchPromptTemplatesSuccess,

  [fetchSinglePromptTemplateSuccess.toString()]: handleFetchSinglePromptTemplateSuccess,
  [fetchSinglePromptTemplateSuccess.toString()]: handleFetchSinglePromptTemplateFailure,
  [fetchSinglePromptTemplateRequest.toString()]: handleFetchSinglePromptTemplateRequest,

  [upsertPromptTemplateRequest.toString()]: handleUpsertPromptTemplateRequest,
  [upsertPromptTemplateFailure.toString()]: handleUpsertPromptTemplateFailure,
  [upsertPromptTemplateSuccess.toString()]: handleUpsertPromptTemplateSuccess,

  [deletePromptTemplateRequest.toString()]: handleDeletePromptTemplateRequest,
  [deletePromptTemplateSuccess.toString()]: handleDeletePromptTemplateSuccess,
  [deletePromptTemplateFailure.toString()]: handleDeletePromptTemplateFailure,
};

const entitiesReducer = createReducer(defaultState, handlers);

export default entitiesReducer;
